import React, { useEffect, useCallback } from 'react';
import { useTranslate } from 'piral-translate';
import { create } from 'zustand';
import { XMarkIcon } from '../Icons/Icons';
import './confirm-dialog.css';

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (() => void) | null;
  onCancel: (() => void) | null;
  open: (options: {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  close: () => void;
}

export const useConfirmDialogStore = create<ConfirmDialogState>((set) => ({
  isOpen: false,
  title: '',
  message: '',
  confirmLabel: undefined,
  cancelLabel: undefined,
  onConfirm: null,
  onCancel: null,
  open: (options) =>
    set({
      isOpen: true,
      title: options.title,
      message: options.message,
      confirmLabel: options.confirmLabel,
      cancelLabel: options.cancelLabel,
      onConfirm: options.onConfirm,
      onCancel: options.onCancel || null,
    }),
  close: () =>
    set({
      isOpen: false,
      title: '',
      message: '',
      confirmLabel: undefined,
      cancelLabel: undefined,
      onConfirm: null,
      onCancel: null,
    }),
}));

export const ConfirmDialog: React.FC = () => {
  const { isOpen, title, message, confirmLabel, cancelLabel, onConfirm, onCancel, close } =
    useConfirmDialogStore();
  const translate = useTranslate();

  const handleConfirm = useCallback(() => {
    onConfirm?.();
    close();
  }, [onConfirm, close]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    close();
  }, [onCancel, close]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleCancel, handleConfirm]);

  if (!isOpen) return null;

  return (
    <div className="neo-confirm-dialog-backdrop" onClick={handleBackdropClick}>
      <div className="neo-confirm-dialog">
        <div className="neo-confirm-dialog-header">
          <h2 className="neo-confirm-dialog-title">{title}</h2>
          <button type="button" className="neo-confirm-dialog-close" onClick={handleCancel}>
            <XMarkIcon />
          </button>
        </div>
        <div className="neo-confirm-dialog-body">
          <p>{message}</p>
        </div>
        <div className="neo-confirm-dialog-actions">
          <button type="button" className="neo-confirm-dialog-cancel" onClick={handleCancel}>
            {cancelLabel || translate('cancel')}
          </button>
          <button type="button" className="neo-confirm-dialog-confirm" onClick={handleConfirm}>
            {confirmLabel || translate('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
